<!--
 * A Vue component that wraps the b-form-textarea element of Bootstrap Vue.
 * This element is required by default.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for input element.
 *
 * - description -
 * (Optional) A description that appears below the input element.
 *
 * - text -
 * (Optional) The text to show on the label above the input field.
 *
 * - state -
 * (Optional) The validation state to set from parent component. state === true => Valid state
 *                                                               state === false => Invalid state
 *
 * - invalidMessage -
 * (Optional) The text to show when state is set to false (invalid).
 *
 * - labelSize -
 * (Optional) To control the label text size. Valid values are 'lg' for large and 'sm' for small.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 * - rows -
 * (Optional) To set number of rows for the textarea (default=2).
 *
 * - cols -
 * (Optional) To set number of cols for the textarea (default=80).
 *
 * - hasResizeHandle -
 * (Optional) Indication if resize handle must be shown or not (default=false).
 *
 * - showCounter -
 * (Optional) Indication if character counter must be shown or not (default=false).
 * Use this in combination with the maxlength attribute
 *
 *
 * Events
 * ==========
 * - input -
 * This event is sent when user changes the input value.
 * The object to sent along with this event is the inserted value from input element.
 *
 * - change -
 * This event is sent when user changes the input value and textarea loses focus.
 * The object to sent along with this event is the inserted value from input element.
 *
 * Usage
 * ==========
 * -You do not need to listen to the emitted input event from this component.
 * Just use the v-model in your parent component and the value will be updated
 * from this component automatically.
 *
 * -You can put the standard html input attributes, such as required,
 * autofocus, pattern, etc. directly from the parent component without explicit binding.
 * The custom attributes like listed above, should be bind.
-->

<template>
    <div>
        <b-form-group
            id="fieldset-1"
            :description="description"
            :label="text"
            :label-for="name"
            :label-class="labelClass"
            :label-size="labelSize"
            :invalid-feedback="invalidFeedback"
            :state="state"
        >
            <b-form-textarea
                v-bind="$attrs"
                :id="id"
                :name="name"
                :no-resize="!hasResizeHandle"
                :value="value"
                :size="labelSize"
                :state="state"
                :rows="rows"
                :cols="cols"
                @input="_onInput"
                @change="$_onChange"
                trim
            >
            </b-form-textarea>
        </b-form-group>
        <span v-if="showCounter" class="message-counter"
            >{{ counter }}/{{ $attrs['maxlength'] }}</span
        >
    </div>
</template>

<script>
    export default {
        inheritAttrs: false,
        name: 'x-textarea',
        props: {
            description: { type: String, required: false },
            id: { type: String, required: false },
            name: { type: String, required: false, default: 'x-textarea-input' },
            text: { type: String, required: false, default: '' },
            state: { type: Boolean, required: false, default: null },
            invalidMessage: { type: String, required: false, default: '' },
            labelSize: { type: String, required: false, default: '' },
            value: [String, Number],
            rows: { type: Number, required: false, default: 2 },
            cols: { type: Number, required: false, default: 80 },
            hasResizeHandle: { type: Boolean, required: false, default: false },
            showCounter: { type: Boolean, required: false, default: false },
            labelClass: { type: String, default: 'font-weight-normal' }
        },

        data() {
            return { inputDirty: false };
        },

        computed: {
            invalidFeedback() {
                if (this.value && this.value.length > 0 && !this.state) {
                    return this.invalidMessage;
                } else if (this.value && this.value.length === 0) {
                    return this.$t('requiredField');
                } else {
                    return '';
                }
            },

            counter() {
                return this.value?.length ?? 0;
            }
        },

        methods: {
            _onInput: function (input) {
                this.inputDirty = true;
                this.$emit('input', input);
            },

            $_onChange(input) {
                this.$emit('change', input);
            }
        }
    };
</script>

<style scoped lang="scss">
.message-counter {
    position: absolute;
    right: 15px;
    bottom: -22px;
    color: $gray-05;
    font-size: 14px;
}
</style>
